import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "../lib/supabase";
import type { User } from "@supabase/supabase-js";

interface UserMetadata {
  first_name?: string;
  last_name?: string;
  organization_name?: string;
  industry_type?: 'service' | 'real_estate';
  property_type?: 'senior_living' | 'apartments' | 'commercial' | 'mixed';
  facility_tiles?: string[];
  organization_address?: string;
  organization_phone?: string;
  organization_email?: string;
  organization_website?: string;
  organization_description?: string;
  [key: string]: any; // Allow additional metadata fields
}

interface UserProfile {
  id: string;
  email: string;
  org_id: string;
  role: "org_admin" | "facility_admin" | "staff";
  user_metadata?: UserMetadata;
}

interface AuthContextType {
  user: UserProfile | null;
  authUser: User | null;
  loading: boolean;
  error: Error | null;
  facilityIds: string[];
  facilityIdsLoading: boolean;
  isEmailVerified: boolean;
  canAccessFacility: (facilityId: string) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [facilityIds, setFacilityIds] = useState<string[]>([]);
  const [facilityIdsLoading, setFacilityIdsLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const loadFacilityAccess = useCallback(async (userProfile: UserProfile) => {
    try {
      setFacilityIdsLoading(true);

      // Org admins have access to all facilities in their org
      if (userProfile.role === "org_admin") {
        const { data: facilities, error: facilitiesError } = await supabase
          .from("facilities")
          .select("id")
          .eq("org_id", userProfile.org_id);

        if (facilitiesError) throw facilitiesError;

        const ids = facilities?.map((f) => f.id) || [];
        setFacilityIds(ids);
        return;
      }

      // Facility admins and staff only see assigned facilities
      const { data: assignments, error: assignmentsError } = await supabase
        .from("user_facility_assignments")
        .select("facility_id")
        .eq("user_id", userProfile.id);

      if (assignmentsError) throw assignmentsError;

      const ids = assignments?.map((a) => a.facility_id) || [];
      setFacilityIds(ids);
    } catch (err) {
      console.error("Error loading facility access:", err);
      setFacilityIds([]);
    } finally {
      setFacilityIdsLoading(false);
    }
  }, []);

  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      setAuthUser(authUser);
      setIsEmailVerified(!!authUser?.email_confirmed_at);

      if (!authUser) {
        setUser(null);
        setFacilityIds([]);
        setIsEmailVerified(false);
        setLoading(false);
        setFacilityIdsLoading(false);
        return;
      }

      const { data: userProfile, error: profileError } = await supabase
        .from("users")
        .select("id, org_id, role")
        .eq("id", authUser.id)
        .single();

      if (profileError) throw profileError;

      if (!userProfile) {
        setUser(null);
        setFacilityIds([]);
        setIsEmailVerified(false);
        setLoading(false);
        setFacilityIdsLoading(false);
        return;
      }

      const newUser = {
        id: userProfile.id,
        email: authUser.email!,
        org_id: userProfile.org_id,
        role: userProfile.role,
        user_metadata: authUser.user_metadata,
      };

      setUser(newUser);
      setLoading(false);

      // Load facility access in parallel
      loadFacilityAccess(newUser);
    } catch (err) {
      console.error("Error loading user:", err);
      setError(err instanceof Error ? err : new Error("Failed to load user"));
      setUser(null);
      setFacilityIds([]);
      setIsEmailVerified(false);
      setLoading(false);
      setFacilityIdsLoading(false);
    }
  }, [loadFacilityAccess]);

  useEffect(() => {
    loadUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      loadUser();
    });

    return () => subscription.unsubscribe();
  }, [loadUser]);

  const canAccessFacility = useCallback(
    (facilityId: string): boolean => {
      if (!user) return false;
      if (user.role === "org_admin") return true;
      return facilityIds.includes(facilityId);
    },
    [user, facilityIds]
  );

  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        authUser,
        loading,
        error,
        facilityIds,
        facilityIdsLoading,
        isEmailVerified,
        canAccessFacility,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
